// NerdGames — game registry.
//
// V44: every game in the system, with its family + route + tagline +
// optional historic figure for badge ladder (Pythagoras / Euclid / etc).
// Family subgrid pages filter this; gamePromo.js reads this for rotation.
//
// Adding a new game: add an entry here. Route wiring still lives in
// App.jsx (router knows what component to mount); this registry is the
// metadata layer.

export const GAMES = [
  // ─── NerdMath family ─────────────────────────────────────────────
  {
    id: 'menage-a-trig',
    name: 'Ménage à Trig',
    family: 'math',
    path: '/games/math/menage-a-trig',
    tagline: 'unit circle by depth — degrees, radians, sin/cos/tan',
  },
  {
    id: 'mathic-quest',
    name: 'Mathic Quest',
    family: 'math',
    path: '/games/math/mathic-quest',
    tagline: 'number-sense fluency — fractions, powers, divisibility',
  },
  {
    id: 'numberline-drop',
    name: 'Number Line Drop',
    family: 'math',
    path: '/games/math/numberline-drop',
    tagline: 'place π, e, √2 on a number line — feel where they live',
  },
  {
    id: 'slope-slider',
    name: 'Slope Slider',
    family: 'math',
    path: '/games/math/slope-slider',
    tagline: 'rise over run, by feel — slope intuition before formula',
  },
  {
    id: 'trajectory',
    name: 'Trajectory',
    family: 'math',
    path: '/games/math/trajectory',
    tagline: 'aim the parabola — projectile motion as game',
  },
  {
    id: 'funcion',
    name: 'Funcion',
    family: 'math',
    path: '/games/math/funcion',
    tagline: 'identify the function family from its behavior',
  },
  {
    id: 'area-architect',
    name: 'Area Architect',
    family: 'math',
    path: '/games/math/area-architect',
    tagline: 'compose areas from shapes — geometry as construction',
  },
  {
    id: 'transform-tetris',
    name: 'Transform Tetris',
    family: 'math',
    path: '/games/math/transform-tetris',
    tagline: 'rotate, reflect, translate — transformations by name',
  },
  {
    id: 'estimate',
    name: 'Estimate',
    family: 'math',
    path: '/games/math/estimate',
    tagline: 'Fermi estimation — order-of-magnitude reasoning',
  },
  {
    id: 'triangle-forge',
    name: 'Triangle Forge',
    family: 'math',
    path: '/games/math/triangle-forge',
    tagline: 'build named triangles — 30-60-90, golden gnomon, more',
  },
  {
    id: 'modular',
    name: 'Modular',
    family: 'math',
    path: '/games/math/modular',
    tagline: 'clock arithmetic — modular orbits, primitive roots',
  },
  {
    id: 'constructor',
    name: 'Constructor',
    family: 'math',
    path: '/games/math/constructor',
    tagline: 'classical Euclidean construction — compass and straightedge',
  },

  // ─── NerdChem family ─────────────────────────────────────────────
  {
    id: 'periodic-quest',
    name: 'Periodic Quest',
    family: 'chem',
    path: '/games/chem/periodic-quest',
    tagline: 'master the periodic table by depth',
  },
  {
    id: 'chemforge',
    name: 'ChemForge',
    family: 'chem',
    path: '/games/chem/chemforge',
    tagline: "build atoms from protons, neutrons, and electrons — Mendeleev's table by discovery",
  },
  {
    id: 'moleforge',
    name: 'MoleForge',
    family: 'chem',
    path: '/games/chem/moleforge',
    tagline: "bond atoms into molecules — Lewis' octets, Kekulé's rings",
  },

  // ─── NerdBio family ──────────────────────────────────────────────
  {
    id: 'gene-pool',
    name: 'Gene Pool',
    family: 'bio',
    path: '/games/bio/gene-pool',
    tagline: 'master the genetic code by depth',
  },
  {
    id: 'anatomy-park',
    name: 'Anatomy Park',
    family: 'bio',
    path: '/games/bio/anatomy-park',
    tagline: 'learn anatomy by depth',
  },
  {
    id: 'pedigree',
    name: 'Pedigree',
    family: 'bio',
    path: '/games/bio/pedigree',
    tagline: 'identify inheritance patterns from family trees',
  },
  {
    id: 'phase-lab',
    name: 'Phase Lab',
    family: 'bio',
    path: '/games/bio/phase-lab',
    tagline: 'drive a cell through mitosis and meiosis',
  },
  {
    id: 'selection',
    name: 'Selection Pressure',
    family: 'bio',
    path: '/games/bio/selection',
    tagline: 'evolution as Hardy-Weinberg violated — selection, drift, migration',
  },
  {
    id: 'codon-reader',
    name: 'Codon Reader',
    family: 'bio',
    path: '/games/bio/codon-reader',
    tagline: 'find ORFs, translate to protein, classify mutations',
  },
  {
    id: 'membrane',
    name: 'Membrane',
    family: 'bio',
    path: '/games/bio/membrane',
    tagline: 'transport across the membrane — pumps, gradients, potential',
  },

  // ─── NerdPhys family ─────────────────────────────────────────────
  {
    id: 'quarkforge',
    name: 'QuarkForge',
    family: 'phys',
    path: '/games/phys/quarkforge',
    tagline: 'build real hadrons from quarks — color charge, Standard Model',
  },
  // V63.3 — first orbital-mechanics game. Mission planner: pick a real
  // target body, work out the patched-conics ΔV, set a burn sequence,
  // launch within a synodic window, simulate the trajectory.
  {
    id: 'mission-control',
    name: 'Mission Control',
    family: 'phys',
    path: '/games/phys/mission-control',
    tagline: 'plan orbital missions — real ΔV budgets, real launch windows',
  },

  // ─── NerdHistory family ──────────────────────────────────────────
  {
    id: 'roman-province',
    name: 'Roman Province',
    family: 'history',
    path: '/games/history/roman-province',
    tagline: 'govern Aquitania — survive your term, please Rome',
  },

  // ─── NerdMusic family ────────────────────────────────────────────
  // V63.2 — first NerdMusic game.
  {
    id: 'film-scoring',
    name: 'Film Scoring',
    family: 'music',
    path: '/games/music/film-scoring',
    tagline: 'score a thriller — five cues, three meters, audible consequence',
  },
];

export function gamesInFamily(familyId) {
  return GAMES.filter(g => g.family === familyId);
}

export function getGameById(id) {
  return GAMES.find(g => g.id === id) || null;
}

export function getGameByPath(path) {
  return GAMES.find(g => g.path === path) || null;
}
